import type { GridDragPoint, GridDragShape } from './dragging';

interface GridDragTransientStateGrid {
  preferredGridDragObject?: GridDragShape;
  hoveredGridDragObject?: GridDragShape;
  hoveredGridDragPoint?: GridDragPoint;
}

interface CreateGridDragTransientStateOptions {
  grid: GridDragTransientStateGrid;
  paintCanvas: () => void;
  isActiveDragOwned?: () => boolean;
  resetActiveDrag?: () => void;
}

interface ClearGridDragTransientStateOptions {
  clearActiveDrag?: boolean;
  repaint?: boolean;
}

export interface GridDragTransientStateController {
  clear: (options?: ClearGridDragTransientStateOptions) => boolean;
  setPreferredObject: (obj?: GridDragShape) => void;
  setHoveredObject: (obj?: GridDragShape) => void;
  setHoveredPoint: (point?: GridDragPoint) => void;
}

export const createGridDragTransientState = ({
  grid,
  paintCanvas,
  isActiveDragOwned,
  resetActiveDrag,
}: CreateGridDragTransientStateOptions): GridDragTransientStateController => {
  const setPreferredObject = (obj?: GridDragShape): void => {
    grid.preferredGridDragObject = obj;
  };

  const setHoveredObject = (obj?: GridDragShape): void => {
    if (obj) {
      setPreferredObject(obj);
    }

    if (grid.hoveredGridDragObject === obj && !grid.hoveredGridDragPoint) {
      return;
    }

    grid.hoveredGridDragObject = obj;
    grid.hoveredGridDragPoint = undefined;
    paintCanvas();
  };

  const setHoveredPoint = (point?: GridDragPoint): void => {
    if (grid.hoveredGridDragPoint === point && !grid.hoveredGridDragObject) {
      return;
    }

    grid.hoveredGridDragObject = undefined;
    grid.hoveredGridDragPoint = point;
    paintCanvas();
  };

  const clear = (options?: ClearGridDragTransientStateOptions): boolean => {
    const clearsActiveDrag = Boolean(
      options?.clearActiveDrag && isActiveDragOwned?.()
    );
    const hadTransientState = Boolean(
      grid.preferredGridDragObject ||
        grid.hoveredGridDragObject ||
        grid.hoveredGridDragPoint ||
        clearsActiveDrag
    );

    grid.preferredGridDragObject = undefined;
    grid.hoveredGridDragObject = undefined;
    grid.hoveredGridDragPoint = undefined;

    if (clearsActiveDrag) {
      resetActiveDrag?.();
    }

    if (hadTransientState && options?.repaint !== false) {
      paintCanvas();
    }

    return hadTransientState;
  };

  return {
    clear,
    setPreferredObject,
    setHoveredObject,
    setHoveredPoint,
  };
};
